export * from './analyzer';
export class VaultCrypto {
    provider;
    constructor(provider) {
        this.provider = provider;
    }
    /**
     * Generates the Auth Key (sent to the server)
     */
    async generateAuthKey(masterPassword, email) {
        const salt = email.toLowerCase() + 'auth';
        return this.provider.deriveKeyArgon2id(masterPassword, salt, 3, 65536, 4, 32);
    }
    /**
     * Generates the Master Encryption Key (MEK) (never sent to server)
     */
    async generateMEK(masterPassword, email) {
        const salt = email.toLowerCase() + 'encryption';
        return this.provider.deriveKeyArgon2id(masterPassword, salt, 3, 65536, 4, 32);
    }
    /**
     * Generate a fresh 32-byte Vault Key
     */
    generateVaultKey() {
        return this.provider.randomBytes(32);
    }
    generateItemKey() {
        return this.provider.randomBytes(32);
    }
    /**
     * Wrap the Vault Key with the MEK
     */
    async wrapVaultKey(vaultKeyBase64, mekBase64) {
        const iv = this.provider.randomBytes(12);
        const { ciphertext, authTag } = await this.provider.encryptAESGCM(mekBase64, iv, vaultKeyBase64);
        return {
            v: 1,
            iv,
            ct: ciphertext,
            mac: authTag,
        };
    }
    /**
     * Unwrap the Vault Key using the MEK
     */
    async unwrapVaultKey(wrapped, mekBase64) {
        if (wrapped.v !== 1)
            throw new Error('Unsupported encryption version');
        return this.provider.decryptAESGCM(mekBase64, wrapped.iv, wrapped.ct, wrapped.mac);
    }
    async wrapItemKey(itemKeyBase64, vaultKeyBase64, itemId, aadPrefix = 'item-key') {
        const iv = this.provider.randomBytes(12);
        const { ciphertext, authTag } = await this.provider.encryptAESGCM(vaultKeyBase64, iv, itemKeyBase64, `${aadPrefix}:${itemId}`);
        return {
            v: 1,
            iv,
            ct: ciphertext,
            mac: authTag,
        };
    }
    async unwrapItemKey(wrapped, vaultKeyBase64, itemId, aadPrefix = 'item-key') {
        if (wrapped.v !== 1) {
            throw new Error('Unsupported encryption version');
        }
        return this.provider.decryptAESGCM(vaultKeyBase64, wrapped.iv, wrapped.ct, wrapped.mac, `${aadPrefix}:${itemId}`);
    }
    async extractItemKey(payload, vaultKeyBase64, itemId) {
        if (!payload.keyIv || !payload.keyCt || !payload.keyMac) {
            return vaultKeyBase64;
        }
        return this.unwrapItemKey({ v: payload.v, iv: payload.keyIv, ct: payload.keyCt, mac: payload.keyMac }, vaultKeyBase64, itemId);
    }
    /**
     * Encrypt a vault item (or any data) using the Vault Key
     */
    async encryptItem(plaintextData, vaultKeyBase64, itemId) {
        const itemKey = this.generateItemKey();
        const wrappedItemKey = await this.wrapItemKey(itemKey, vaultKeyBase64, itemId);
        const iv = this.provider.randomBytes(12);
        const { ciphertext, authTag } = await this.provider.encryptAESGCM(itemKey, iv, plaintextData, `vault-item:${itemId}`);
        return {
            v: 1,
            iv,
            ct: ciphertext,
            mac: authTag,
            keyIv: wrappedItemKey.iv,
            keyCt: wrappedItemKey.ct,
            keyMac: wrappedItemKey.mac,
        };
    }
    /**
     * Decrypt a vault item using the Vault Key
     */
    async decryptItem(payload, vaultKeyBase64, itemId) {
        if (payload.v !== 1)
            throw new Error('Unsupported encryption version');
        if (payload.keyIv && payload.keyCt && payload.keyMac) {
            const itemKey = await this.extractItemKey(payload, vaultKeyBase64, itemId);
            return this.provider.decryptAESGCM(itemKey, payload.iv, payload.ct, payload.mac, `vault-item:${itemId}`);
        }
        return this.provider.decryptAESGCM(vaultKeyBase64, payload.iv, payload.ct, payload.mac, itemId);
    }
}
